import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaTimes, FaInfoCircle, FaEnvelope, FaCheck, FaUndo, FaEye } from 'react-icons/fa';
import axios from '../axiosInstance';
import { UserContext } from '../UserContext';
import { exportBrandedPdf, PDF_COLOURS } from '../utils/brandedPdf';
import { FEEDBACK_RATINGS, FEEDBACK_CATEGORIES } from '../components/FeedbackButton';
import '../Pips/Pips.css';
import '../UnSanctions/UnSanctions.css';
import '../Pages.css';
import '../Customers/Customers.css';

// Feedback sent with the floating Feedback button. PIP Intel staff only
// (users without an organisation) — the API enforces the same rule.

const STATUS_LABEL = { new: 'New', in_review: 'In review', resolved: 'Resolved' };
// Reuse the KYC status pill colours.
const STATUS_PILL = { new: 'pending', in_review: 'suspended', resolved: 'active' };
const categoryOf = (v) => FEEDBACK_CATEGORIES.find((c) => c.v === v) || { label: v, icon: null };
const ratingOf = (v) => FEEDBACK_RATINGS.find((r) => r.v === v);
// Sent from the login page (not signed in): the organisation is whatever they typed.
const orgOf = (r) => r.organisation_name || (r.user_id ? 'PIP Intel' : 'Not given');

const formatWhen = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

function ManageFeedback() {
  const { user } = useContext(UserContext);
  const isStaff = !!user && !user.organisation_id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/feedback');
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not load feedback.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isStaff) fetchList(); }, [isStaff, fetchList]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const res = await axios.patch(`/feedback/${id}/status`, { status });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not update the status.');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return rows.filter((r) =>
      (statusFilter === 'all' || r.status === statusFilter) &&
      (categoryFilter === 'all' || r.category === categoryFilter) &&
      (ratingFilter === 'all' || (ratingFilter === 'none' ? r.rating == null : r.rating === Number(ratingFilter))) &&
      (!q || [r.message, r.user_name, r.user_email, r.organisation_name, r.page]
        .some((v) => String(v || '').toLowerCase().includes(q)))
    );
  }, [rows, searchTerm, statusFilter, categoryFilter, ratingFilter]);

  const countBy = (fn) => rows.filter(fn).length;
  const rated = rows.filter((r) => r.rating != null);
  const avgRating = rated.length ? (rated.reduce((s, r) => s + r.rating, 0) / rated.length).toFixed(1) : null;

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const page = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const exportPDF = async () => {
    if (!filtered.length) return;
    const filterParts = [
      searchTerm.trim() && `"${searchTerm.trim()}"`,
      statusFilter !== 'all' && STATUS_LABEL[statusFilter],
      categoryFilter !== 'all' && categoryOf(categoryFilter).label,
      ratingFilter !== 'all' && (ratingFilter === 'none' ? 'No rating' : `Rating ${ratingFilter}/5`)
    ].filter(Boolean);
    const STATUS_COLOUR = { new: PDF_COLOURS.navy, in_review: PDF_COLOURS.orange, resolved: PDF_COLOURS.green };

    await exportBrandedPdf({
      title: 'CUSTOMER FEEDBACK — REPORT',
      user,
      searchValue: filterParts.length ? filterParts.join('  ·  ') : 'All feedback',
      fileName: 'Customer_Feedback_Report',
      columns: [
        { header: 'Date', width: 10 },
        { header: 'From', width: 15, bold: true },
        { header: 'Organisation', width: 12 },
        { header: 'Type', width: 8 },
        { header: 'Rating', width: 7 },
        { header: 'Feedback', width: 34 },
        { header: 'Screen', width: 8 },
        { header: 'Status', width: 6 }
      ],
      rows: filtered.map((r) => [
        formatWhen(r.created_at),
        [r.user_name, r.user_email].filter(Boolean).join('\n') || '—',
        orgOf(r) + (r.user_id ? '' : '\n(login page)'),
        categoryOf(r.category).label,
        r.rating ? `${r.rating}/5 ${ratingOf(r.rating).label}` : '—',
        r.message,
        r.page || '—',
        STATUS_LABEL[r.status]
      ]),
      cellStyle: (row, col) => {
        const r = filtered[row];
        if (col === 7) return { textColor: STATUS_COLOUR[r.status], fontStyle: 'bold' };
        if (col === 4 && r.rating) {
          return { textColor: r.rating >= 4 ? PDF_COLOURS.green : r.rating <= 2 ? PDF_COLOURS.red : PDF_COLOURS.orange, fontStyle: 'bold' };
        }
        return undefined;
      }
    });
  };

  if (!isStaff) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> Feedback is only available to PIP Intel staff.
          </span>
        </div>
      </div>
    );
  }

  const resetPage = () => setCurrentPage(1);

  return (
    <div className="page-container">
      <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
        <div className="stats-grid">
          <div className="stat-card stat-card--blue">
            <div className="stat-label">Total Feedback</div>
            <div className="stat-value stat-value--blue">{rows.length}</div>
          </div>
          <div className="stat-card stat-card--teal">
            <div className="stat-label">New</div>
            <div className="stat-value stat-value--teal">{countBy((r) => r.status === 'new')}</div>
          </div>
          <div className="stat-card stat-card--orange">
            <div className="stat-label">In Review</div>
            <div className="stat-value stat-value--orange">{countBy((r) => r.status === 'in_review')}</div>
          </div>
          <div className="stat-card stat-card--purple">
            <div className="stat-label">Average Rating</div>
            <div className="stat-value stat-value--purple">
              {avgRating ? `${avgRating} / 5` : '—'}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)', marginTop: '0.35rem' }}>
              from {rated.length} rating{rated.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
      </div>
        </div>

      <div style={{ marginBottom: '2rem' }}>
        <div className="table-controls">
          <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Search feedback..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); resetPage(); }}
              className="search-filter"
              style={{ width: '100%', maxWidth: 'none', paddingRight: '2.25rem', boxSizing: 'border-box' }}
            />
            {searchTerm && (
              <span
                role="button"
                tabIndex={0}
                title="Clear search"
                aria-label="Clear search"
                onClick={() => { setSearchTerm(''); resetPage(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setSearchTerm(''); resetPage(); } }}
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-secondary, #607d8b)', display: 'flex' }}
              >
                <FaTimes />
              </span>
            )}
          </div>
          <div className="button-group">
            <button className="export-button" onClick={exportPDF} disabled={!filtered.length}>Export PDF</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert-banner alert-banner--danger">
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {loading && <p>Loading feedback…</p>}

      {!loading && rows.length === 0 && !error && (
        <div className="alert-banner alert-banner--warning">
          <span className="alert-banner__body"><FaInfoCircle /> No feedback has been sent yet.</span>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          <div className="search-results-info" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div className="search-count" style={{ flex: 1, fontSize: '0.8rem' }}>
              Showing {filtered.length} of {rows.length} feedback item{rows.length === 1 ? '' : 's'}
            </div>
            <select
              className="table-search-input"
              style={{ width: 'auto' }}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); resetPage(); }}
              title="Filter by status"
            >
              <option value="all">All statuses</option>
              {Object.keys(STATUS_LABEL).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]} ({countBy((r) => r.status === s)})</option>
              ))}
            </select>
            <select
              className="table-search-input"
              style={{ width: 'auto' }}
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); resetPage(); }}
              title="Filter by type"
            >
              <option value="all">All types</option>
              {FEEDBACK_CATEGORIES.map((c) => (
                <option key={c.v} value={c.v}>{c.label} ({countBy((r) => r.category === c.v)})</option>
              ))}
            </select>
            <select
              className="table-search-input"
              style={{ width: 'auto' }}
              value={ratingFilter}
              onChange={(e) => { setRatingFilter(e.target.value); resetPage(); }}
              title="Filter by rating"
            >
              <option value="all">All ratings</option>
              {[...FEEDBACK_RATINGS].reverse().map((r) => (
                <option key={r.v} value={String(r.v)}>{r.face} {r.label} ({countBy((x) => x.rating === r.v)})</option>
              ))}
              <option value="none">No rating ({countBy((x) => x.rating == null)})</option>
            </select>
          </div>

          <div className="un-card-list">
            {pageItems.map((r, i) => {
              const cat = categoryOf(r.category);
              const rt = ratingOf(r.rating);
              return (
                <div key={r.id} className={`un-card un-card--individual${i % 2 === 1 ? ' un-card--alt' : ''}`}>
                  <div className="un-card__header">
                    <div className="un-card__title-row">
                      <span className="un-card__badge un-card__badge--individual">{cat.icon} {cat.label}</span>
                      <h3 className="un-card__title">{r.user_name || r.user_email || 'Unknown user'}</h3>
                    </div>
                    <div className="un-card__meta">
                      <span className={`un-meta__pill cust-pill--status-${STATUS_PILL[r.status]}`}>Status: {STATUS_LABEL[r.status]}</span>
                      {rt && (
                        <span className={`un-meta__pill ${r.rating >= 4 ? 'cust-pill--risk-low' : r.rating <= 2 ? 'cust-pill--risk-high' : 'cust-pill--risk-medium'}`}>
                          {rt.face} {rt.label} ({r.rating}/5)
                        </span>
                      )}
                      <span className="un-meta__pill">Organisation: {orgOf(r)}</span>
                      {!r.user_id && <span className="un-meta__pill cust-pill--status-pending">Login page · not signed in</span>}
                      {r.page && <span className="un-meta__pill">Screen: {r.page}</span>}
                      <span className="un-meta__pill">{formatWhen(r.created_at)}</span>
                      {!r.email_sent && <span className="un-meta__pill cust-pill--flag">Email not sent</span>}
                    </div>
                  </div>
                  <div className="un-card__body" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {r.message}
                  </div>
                  <div className="un-card__footer" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ flex: 1, fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)' }}>
                      {r.user_email}{r.contact_ok ? ' · happy to be contacted' : ' · prefers not to be contacted'}
                      {r.status_updated_at && ` · ${STATUS_LABEL[r.status]} ${formatWhen(r.status_updated_at)}${r.status_updated_by_name ? ` by ${r.status_updated_by_name}` : ''}`}
                    </span>
                    {r.contact_ok && r.user_email && (
                      <a
                        className="btn-cancel"
                        style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        href={`mailto:${r.user_email}?subject=${encodeURIComponent(`Re: your PIP Intel feedback (#${r.id})`)}`}
                      >
                        <FaEnvelope /> Reply
                      </a>
                    )}
                    {r.status === 'new' && (
                      <button type="button" className="btn-cancel" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'in_review')}>
                        <FaEye style={{ marginRight: 5 }} /> Mark in review
                      </button>
                    )}
                    {r.status !== 'resolved' ? (
                      <button type="button" className="export-button" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'resolved')}>
                        <FaCheck style={{ marginRight: 5 }} /> Mark resolved
                      </button>
                    ) : (
                      <button type="button" className="btn-cancel" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'new')}>
                        <FaUndo style={{ marginRight: 5 }} /> Re-open
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length > 0 && (
            <div className="pagination-controls">
              <div className="pagination-left">
                <span className="pagination-info">
                  {(page - 1) * itemsPerPage + 1}–{Math.min(page * itemsPerPage, filtered.length)} of {filtered.length}
                </span>
              </div>
              <div className="pagination-right">
                <div className="pagination-per-page">
                  <span>Per page:</span>
                  <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); resetPage(); }}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="pagination pagination-pages">
                  <button onClick={() => setCurrentPage(Math.max(1, page - 1))} disabled={page === 1}>← Prev</button>
                  <span className="pagination-info">Page {page} of {totalPages}</span>
                  <button onClick={() => setCurrentPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}>Next →</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ManageFeedback;
